import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { CalendarAPI } from '../../api/CalendarAPI';
import { rootAction } from '../../redux/reducer/rootReducer';
import { AuthContext } from '../auth/AuthContext';
import CustomDialog from '../CustomComponents/CustomDialog/CustomDialog.jsx';
import {
    EXTRA_TIME_COPY,
    HOLD_EXTRA_SECONDS,
    HOLD_PAY_LOCK_SECONDS,
    clearHoldRecord,
    formatHoldClock,
    writeHoldRecord,
} from '../../util/reservationHoldTimer';

const Timer = () => {
    const { setMessage } = useContext(AuthContext);
    const {
        initialTimer,
        isTimerActive,
        disablePayBtn,
        holdAskExtra,
        holdCreatedDate,
        holdExtraGrantedAt,
    } = useSelector((store) => store.root);
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const [, setTimeLeft] = useState(0);
    const dispatch = useDispatch();

    const releaseHold = async () => {
        const clubId = selectedClub?.clubId;
        try {
            if (userInfo?.userId && clubId) {
                await CalendarAPI.deleteReservationWhenNotPaid({ userId: userInfo.userId, clubId });
            }
            clearHoldRecord(clubId, userInfo?.userId);
            dispatch(rootAction.setReservationPaymentStatus({ status: true, type: 'deleted' }));
        } catch (error) {
            // Header refetches pending count from payment status.
        } finally {
            dispatch(rootAction.setDisablePayBtn(false));
            dispatch(rootAction.setIsTimerActive(false));
            dispatch(rootAction.setHoldAskExtra(false));
            dispatch(rootAction.setHoldDisplay(""));
            dispatch(rootAction.setHoldExtraGrantedAt(null));
        }
    };

    const expireHold = async () => {
        setMessage({ type: 'info', content: EXTRA_TIME_COPY.expired });
        setTimeout(() => setMessage({ type: '', content: '' }), 4000);
        await releaseHold();
    };

    const grantExtraTime = () => {
        const extraGrantedAt = new Date().toISOString();
        writeHoldRecord(selectedClub?.clubId, userInfo?.userId, {
            createdDate: holdCreatedDate,
            extraGrantedAt,
        });
        dispatch(rootAction.setHoldExtraGrantedAt(extraGrantedAt));
        dispatch(rootAction.setHoldAskExtra(false));
        dispatch(rootAction.setHoldPhaseSeconds(HOLD_EXTRA_SECONDS));
        dispatch(rootAction.setInitialTimer(HOLD_EXTRA_SECONDS));
        dispatch(rootAction.setIsTimerActive(true));
        dispatch(rootAction.setDisablePayBtn(false));
        setTimeLeft(HOLD_EXTRA_SECONDS);
    };

    useEffect(() => {
        setTimeLeft(Number(initialTimer) || 0);
        dispatch(rootAction.setHoldDisplay(formatHoldClock(initialTimer)));
    }, [initialTimer, isTimerActive, holdAskExtra]);

    useEffect(() => {
        let timer;
        if (isTimerActive && !holdAskExtra && Number(initialTimer) > 0) {
            timer = setInterval(() => {
                setTimeLeft((prevTime) => {
                    const newTimeLeft = prevTime - 1;
                    dispatch(rootAction.setHoldDisplay(formatHoldClock(newTimeLeft)));
                    if (newTimeLeft <= HOLD_PAY_LOCK_SECONDS && !disablePayBtn) {
                        dispatch(rootAction.setDisablePayBtn(true));
                    }
                    if (newTimeLeft < 0) {
                        clearInterval(timer);
                        if (holdExtraGrantedAt || !holdCreatedDate) {
                            expireHold();
                            return 0;
                        }
                        dispatch(rootAction.setIsTimerActive(false));
                        dispatch(rootAction.setHoldAskExtra(true));
                        dispatch(rootAction.setHoldDisplay(formatHoldClock(0)));
                        return 0;
                    }
                    return newTimeLeft;
                });
            }, 1000);
        } else if (!isTimerActive && !holdAskExtra) {
            dispatch(rootAction.setHoldDisplay(""));
        }
        return () => clearInterval(timer);
    }, [initialTimer, isTimerActive, holdAskExtra, holdCreatedDate, holdExtraGrantedAt]);

    useEffect(() => {
        if (holdAskExtra) {
            dispatch(rootAction.setDisablePayBtn(true));
            dispatch(rootAction.setHoldDisplay(formatHoldClock(0)));
        }
    }, [holdAskExtra]);

    return (
        <CustomDialog
            isOpen={Boolean(holdAskExtra)}
            onClose={expireHold}
            onCancel={expireHold}
            onSubmit={grantExtraTime}
            title={EXTRA_TIME_COPY.title}
            submitBtnName={EXTRA_TIME_COPY.confirm}
            cancelBtnName={EXTRA_TIME_COPY.cancel}
        >
            <p data-testid="hold-extra-copy">{EXTRA_TIME_COPY.body}</p>
        </CustomDialog>
    );
};

export default Timer
